import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TenantThemeDocument } from '@vipso/shared';
import type { OrgFeatureRow } from '../utils/moduleLabels';

/** Decode membershipId from a JWT access token (org user sessions). */
export function membershipIdFromJwt(token: string | null | undefined): string | null {
  if (!token || token.split('.').length < 2) return null;
  try {
    const payloadPart = token.split('.')[1]!;
    const b64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json) as { membershipId?: string | number };
    return payload.membershipId != null && payload.membershipId !== ''
      ? String(payload.membershipId)
      : null;
  } catch {
    return null;
  }
}

/** Full backend theme JSON (plus convenience fields for logo/colors). */
export type TenantTheme = TenantThemeDocument & {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  fontFamily?: string | null;
  mode?: string | null;
};

export interface TenantConfig {
  defaultContextLogin: boolean;
  loginType: 'OTP' | 'PASSWORD' | string;
  logoFileId?: string | null;
  logoUrl?: string | null;
  maxOrgs?: number | null;
  maxUsers?: number | null;
  enabledModules?: string[] | null;
}

export interface TenantOrgOption {
  id: string;
  name: string;
  uniqueId?: string | null;
  status?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoFileId?: string | null;
  logoUrl?: string | null;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
}

export interface AuthUser {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  address?: {
    id?: string;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null;
}

export interface OrgRef {
  orgId: string;
  name?: string | null;
  membershipId: string;
}

export interface OrgInfo {
  id?: string;
  orgId?: string;
  name?: string;
  status?: string;
  phone?: string;
  uniqueId?: string;
  tenantId?: string;
  /** IANA timezone for date display */
  timezone?: string;
  businessEmail?: string | null;
  contactPersonPhone?: string | null;
  gstin?: string | null;
  address?: {
    id?: string;
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null;
  logoFileId?: string | null;
  logoUrl?: string | null;
  brandLogoUrl?: string | null;
  primaryColor?: string | null;
  [key: string]: unknown;
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  org: OrgInfo | null;
  /** Current org's membership id (from initApp / JWT). Used for approval step matching. */
  membershipId: string | null;
  featureKeys: string[];
  /** Org feature-set rows (labels + entitlements). */
  orgFeatures: OrgFeatureRow[];
  tenant: TenantInfo | null;
  variantName: string | null;
  theme: TenantTheme | null;
  config: TenantConfig | null;
  tenantOrgs: TenantOrgOption[];
  pendingPhone: string | null;
  pendingOrgId: string | null;
  /** Last successfully entered org — survives logout for auto-login when org select is off. */
  lastOrgId: string | null;
  orgs: OrgRef[];
  socketRooms: string[];
  bootstrapped: boolean;
  pendingTaskCount: number;
  operationsLocked: boolean;
}

const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  org: null,
  membershipId: null,
  featureKeys: [],
  orgFeatures: [],
  tenant: null,
  variantName: null,
  theme: null,
  config: null,
  tenantOrgs: [],
  pendingPhone: null,
  pendingOrgId: null,
  lastOrgId: null,
  orgs: [],
  socketRooms: [],
  bootstrapped: false,
  pendingTaskCount: 0,
  operationsLocked: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTenantContext(
      state,
      action: PayloadAction<{
        tenant: TenantInfo;
        variantName?: string | null;
        theme?: TenantTheme | null;
        config?: TenantConfig | null;
        tenantOrgs?: TenantOrgOption[];
      }>,
    ) {
      state.tenant = action.payload.tenant;
      state.variantName = action.payload.variantName ?? action.payload.tenant.name;
      state.theme = action.payload.theme ?? null;
      state.config = action.payload.config ?? null;
      state.tenantOrgs = action.payload.tenantOrgs ?? [];
    },
    setPendingPhone(state, action: PayloadAction<string>) {
      state.pendingPhone = action.payload;
    },
    setPendingOrgId(state, action: PayloadAction<string | null>) {
      state.pendingOrgId = action.payload;
    },
    setVerifyOtpResult(
      state,
      action: PayloadAction<{ user: AuthUser; orgs: OrgRef[] }>,
    ) {
      state.user = action.payload.user;
      state.orgs = action.payload.orgs;
    },
    setAuthFromInit(
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
        featureKeys: string[];
        orgFeatures?: OrgFeatureRow[];
        socketRooms: string[];
        org?: OrgInfo | null;
        tenant?: TenantInfo | null;
        theme?: TenantTheme | null;
        user?: AuthUser | null;
        orgs?: OrgRef[] | null;
        membershipId?: string | null;
        pendingTaskCount?: number;
        operationsLocked?: boolean;
      }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.featureKeys = action.payload.featureKeys;
      state.orgFeatures = action.payload.orgFeatures ?? [];
      state.socketRooms = action.payload.socketRooms;
      if (action.payload.org) state.org = action.payload.org;
      if (action.payload.tenant) state.tenant = action.payload.tenant as TenantInfo;
      if (action.payload.theme) state.theme = action.payload.theme;
      if (action.payload.user) state.user = action.payload.user as AuthUser;
      state.pendingTaskCount = action.payload.pendingTaskCount ?? 0;
      state.operationsLocked = Boolean(action.payload.operationsLocked);
      state.pendingPhone = null;
      state.pendingOrgId = null;
      // Memberships must survive init/refresh — the org switcher reads them.
      if (action.payload.orgs?.length) state.orgs = action.payload.orgs;

      const orgId = String(state.org?.id ?? state.org?.orgId ?? '');
      const fromOrgs = state.orgs.find((o) => String(o.orgId) === orgId)?.membershipId;
      const fromJwt = membershipIdFromJwt(action.payload.accessToken);
      state.membershipId =
        (action.payload.membershipId != null && action.payload.membershipId !== ''
          ? String(action.payload.membershipId)
          : null) ??
        (fromOrgs != null ? String(fromOrgs) : null) ??
        fromJwt;
      if (orgId) state.lastOrgId = orgId;
    },
    setPendingTaskCount(state, action: PayloadAction<number>) {
      state.pendingTaskCount = action.payload;
    },
    patchOrg(state, action: PayloadAction<Partial<OrgInfo>>) {
      if (!state.org) {
        state.org = { ...action.payload };
        return;
      }
      state.org = { ...state.org, ...action.payload };
    },
    patchUser(state, action: PayloadAction<Partial<AuthUser>>) {
      if (!state.user) {
        state.user = { id: '', name: '', phone: '', ...action.payload } as AuthUser;
        return;
      }
      state.user = { ...state.user, ...action.payload };
    },
    setTokens(
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },
    setBootstrapped(state, action: PayloadAction<boolean>) {
      state.bootstrapped = action.payload;
    },
    /** Preview org brand on login/OTP while picking an organization. */
    patchAuthTheme(
      state,
      action: PayloadAction<{
        primaryColor?: string | null;
        secondaryColor?: string | null;
        logoUrl?: string | null;
      }>,
    ) {
      const next = { ...(state.theme ?? {}) } as TenantTheme;
      if (action.payload.primaryColor !== undefined) {
        next.primaryColor = action.payload.primaryColor ?? undefined;
      }
      if (action.payload.secondaryColor !== undefined) {
        next.secondaryColor = action.payload.secondaryColor ?? undefined;
      }
      if (action.payload.logoUrl !== undefined) {
        next.logoUrl = action.payload.logoUrl;
      }
      // Drop tenant brand palette so Mantine regenerates shades from primary hex
      if (next.colors && typeof next.colors === 'object' && !Array.isArray(next.colors)) {
        const colors = { ...(next.colors as Record<string, unknown>) };
        delete colors.brand;
        next.colors = colors as TenantTheme['colors'];
      }
      state.theme = next;
    },
    logout(state) {
      // Keep bootstrapped so we stay on /login (AppBootstrap already ran).
      // Keep public tenant scope + lastOrgId for next login defaults.
      // Preserve in-progress OTP so a failed stale-session refresh on /otp
      // does not wipe pendingPhone and bounce the user away mid-verify.
      return {
        ...initialState,
        bootstrapped: true,
        tenant: state.tenant,
        variantName: state.variantName,
        theme: state.theme,
        config: state.config,
        tenantOrgs: state.tenantOrgs,
        lastOrgId: state.lastOrgId,
        pendingPhone: state.pendingPhone,
        pendingOrgId: state.pendingOrgId,
      };
    },
  },
});

export const {
  setTenantContext,
  setPendingPhone,
  setPendingOrgId,
  setVerifyOtpResult,
  setAuthFromInit,
  setPendingTaskCount,
  patchOrg,
  patchUser,
  setTokens,
  setBootstrapped,
  patchAuthTheme,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
